import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import FlexBetween from "../../components/FlexBetween";
import Loader from "../../components/Molecules/Loader/Loader";
import CommentBeforeUpdateDialog from "../../components/Dialogs/CommentBeforeUpdateDialog";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { validateConfigRequest } from "../../services/resourcesService";
import { useToast, extractApiErrorMessage } from "../../services/utils";
import { getDifferentValues } from "../../services/utils/processRuleExecutionResourcesData";
import {
  createGenericRQScoringConfig,
  getGenericRQScoringConfigById,
  updateGenericRQScoringConfig,
} from "../../services/scoringService";
import useFetchGenericRQScoringConfigById from "../../hooks/useFetchGenericRQScoringConfigById";
import {
  genericRQScoringConfigDefaults,
  scoringSeverityOptions,
} from "../../services/constants";
import { validationScoringConfigFormSchema } from "../../schemas";
import {
  getImpactSumError,
  isScoringSubmitDisabled,
} from "../../utils/scoringConfigFormValidation";
import { SLIDER_ACCENT } from "../../components/Scoring/scoreTheme";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import AutoCompleteGenericResearchQueryId from "../../components/Molecules/GenericResearchQuery/AutoCompleteGenericResearchQueryId";
import { getGenericResearchQueryDetail } from "../../services/ResearchQueriesService";

const SLIDER_SX = {
  color: SLIDER_ACCENT,
  "& .MuiSlider-thumb": { color: SLIDER_ACCENT },
  "& .MuiSlider-track": { color: SLIDER_ACCENT, border: "none" },
  "& .MuiSlider-rail": { color: SLIDER_ACCENT, opacity: 0.3 },
  "& .MuiSlider-valueLabel": { backgroundColor: SLIDER_ACCENT },
  "& .MuiSlider-markActive": { backgroundColor: SLIDER_ACCENT },
};

const IMPACT_MARKS = [
  { value: 0, label: "0" },
  { value: 0.5, label: "0.5" },
  { value: 1, label: "1" },
];

const AddEditGenericRQScoring: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { id: configId } = useParams();
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const isEditMode = Boolean(configId);
  const [isLoading, setIsLoading] = useState(false);
  const [openComment, setOpenComment] = useState(false);
  const [comment, setComment] = useState("");
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [genericResearchQueryId, setGenericResearchQueryId] = useState<
    number | ""
  >("");
  const [grqError, setGrqError] = useState<string>("");
  const [grqName, setGrqName] = useState<string>("");
  const [severityMappings, setSeverityMappings] = useState<
    Record<string, string>
  >({});
  const [initialSeverityMappings, setInitialSeverityMappings] = useState<
    Record<string, string>
  >({});
  const [queryNameOptions, setQueryNameOptions] = useState<string[]>([]);
  const newMappingCounter = useRef(0);

  const [configData] = useFetchGenericRQScoringConfigById({
    setIsLoading,
    configId: configId ? parseInt(configId) : undefined,
  });
  const [localConfigOverride, setLocalConfigOverride] = useState<any>(null);
  const effectiveConfig = localConfigOverride || configData;
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);

  const formik = useFormik({
    initialValues: {
      impact_of_record_level:
        effectiveConfig?.impact_of_record_level ??
        genericRQScoringConfigDefaults.impact_of_record_level,
      impact_of_issue_level:
        effectiveConfig?.impact_of_issue_level ??
        genericRQScoringConfigDefaults.impact_of_issue_level,
      factor_low:
        effectiveConfig?.factor_low ??
        genericRQScoringConfigDefaults.factor_low,
      factor_medium:
        effectiveConfig?.factor_medium ??
        genericRQScoringConfigDefaults.factor_medium,
      factor_high:
        effectiveConfig?.factor_high ??
        genericRQScoringConfigDefaults.factor_high,
    },
    validationSchema: validationScoringConfigFormSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const hasEmptyOrTempKey = Object.keys(severityMappings).some(
        (k) => k === "" || k.startsWith("__new_"),
      );
      if (hasEmptyOrTempKey) {
        showToast(
          "Please select a query name for all severity mappings before saving.",
          "error",
        );
        return;
      }
      const cleanMappings = Object.fromEntries(
        Object.entries(severityMappings).filter(
          ([k]) => k !== "" && !k.startsWith("__new_"),
        ),
      );
      if (!isEditMode) {
        if (!genericResearchQueryId) {
          setGrqError("Research Query is required");
          return;
        }
        setIsLoading(true);
        try {
          await createGenericRQScoringConfig({
            generic_research_query_id: Number(genericResearchQueryId),
            impact_of_record_level: values.impact_of_record_level,
            impact_of_issue_level: values.impact_of_issue_level,
            factor_low: values.factor_low,
            factor_medium: values.factor_medium,
            factor_high: values.factor_high,
            severity_mappings: cleanMappings,
          });
          showToast("Generic RQ scoring config created!", "success");
          navigate("/scoring-config?tab=0&runSub=2");
        } catch (error) {
          showToast(
            extractApiErrorMessage(error, "Failed to create config"),
            "error",
          );
        } finally {
          setIsLoading(false);
        }
      } else {
        const diff = getDifferentValues(values, formik.initialValues);
        const mappingsChanged =
          JSON.stringify(initialSeverityMappings) !==
          JSON.stringify(cleanMappings);
        if ((!diff || Object.keys(diff).length === 0) && !mappingsChanged) {
          showToast("No changes detected", "warning");
          return;
        }
        setPendingPayload({ ...values, severity_mappings: cleanMappings });
        setOpenComment(true);
      }
    },
  });

  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: isEditMode ? "Edit Generic RQ Scoring" : "Add Generic RQ Scoring",
      id: configId ? parseInt(configId) : null,
    });
    return () => setCurrentBreadcrumbPage({ name: "", id: null });
  }, [configId]);

  useEffect(() => {
    if (effectiveConfig?.generic_research_query_id) {
      setGenericResearchQueryId(effectiveConfig.generic_research_query_id);
    }
    if (effectiveConfig) {
      const mappings = effectiveConfig.severity_mappings || {};
      setSeverityMappings(mappings);
      setInitialSeverityMappings(JSON.parse(JSON.stringify(mappings)));
    }
  }, [effectiveConfig]);

  // Fetch GRQ display name + research_queries names for severity mapping options
  useEffect(() => {
    if (!genericResearchQueryId) {
      setQueryNameOptions([]);
      return;
    }
    (async () => {
      try {
        const data = await getGenericResearchQueryDetail(
          genericResearchQueryId as number,
        );
        const name = data?.name || data?.generic_research_query?.name;
        if (name) setGrqName(name);
        const queries =
          data?.research_queries ||
          data?.generic_research_query?.research_queries ||
          [];
        const names = queries
          .map((q: any) => q?.name)
          .filter((n: any) => Boolean(n));
        setQueryNameOptions(names);
      } catch (err) {
        console.error("Failed to load generic research query details:", err);
      }
    })();
  }, [genericResearchQueryId]);

  const handleChangeGRQ = (_event: any, value: any) => {
    setGenericResearchQueryId(value?.id ?? "");
    if (grqError) setGrqError("");
    // Reset severity mappings when switching to a different GRQ since
    // query-name options come from the selected GRQ's research_queries.
    setSeverityMappings({});
  };

  const handleSaveComment = async () => {
    setOpenComment(false);
    setIsLoading(true);
    try {
      const payload: any = {};
      Object.keys(pendingPayload).forEach((k) => {
        const val = pendingPayload[k];
        if (k === "severity_mappings") {
          payload[k] = val;
        } else {
          payload[k] = isNaN(Number(val)) ? val : Number(val);
        }
      });
      await updateGenericRQScoringConfig(parseInt(configId!), {
        ...payload,
        comment,
      });
      showToast("Config updated!", "success");
      // Refetch from server so enableReinitialize picks up fresh data
      const refreshed = await getGenericRQScoringConfigById(
        parseInt(configId!),
      );
      if (refreshed) {
        setLocalConfigOverride(refreshed);
        const newMappings = refreshed.severity_mappings || {};
        setSeverityMappings(newMappings);
        setInitialSeverityMappings(JSON.parse(JSON.stringify(newMappings)));
      }
    } catch (error) {
      showToast(
        extractApiErrorMessage(error, "Failed to update config"),
        "error",
      );
    } finally {
      setIsLoading(false);
      setComment("");
      setPendingPayload(null);
    }
  };

  const buildPayload = () => {
    const cleanMappings = Object.fromEntries(
      Object.entries(severityMappings).filter(
        ([k]) => k !== "" && !k.startsWith("__new_"),
      ),
    );
    if (isEditMode) {
      // Mirrors the body sent by handleSaveComment to updateGenericRQScoringConfig.
      // Uses the live `comment` state so the View JSON always reflects the
      // exact payload that would be sent if Save were clicked right now.
      // Include generic_research_query_id so Config can create this entity.
      return {
        generic_research_query_id: Number(genericResearchQueryId),
        impact_of_record_level: formik.values.impact_of_record_level,
        impact_of_issue_level: formik.values.impact_of_issue_level,
        factor_low: formik.values.factor_low,
        factor_medium: formik.values.factor_medium,
        factor_high: formik.values.factor_high,
        severity_mappings: cleanMappings,
        comment,
      };
    }
    return {
      generic_research_query_id: Number(genericResearchQueryId),
      impact_of_record_level: formik.values.impact_of_record_level,
      impact_of_issue_level: formik.values.impact_of_issue_level,
      factor_low: formik.values.factor_low,
      factor_medium: formik.values.factor_medium,
      factor_high: formik.values.factor_high,
      severity_mappings: cleanMappings,
    };
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: isEditMode
          ? `/research-query-scoring-config/${configId}`
          : "/research-query-scoring-config",
        method: isEditMode ? "PUT" : "POST",
        body: payload,
        query_params: {},
        url_params: isEditMode ? { id: configId } : {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) showToast("Payload is valid!", "success");
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyPayload = async () => {
    await navigator.clipboard.writeText(
      JSON.stringify(buildPayload(), null, 2),
    );
    showToast("Payload copied to clipboard!", "success");
  };

  const handleResetToDefaults = () => {
    formik.setValues({
      impact_of_record_level:
        genericRQScoringConfigDefaults.impact_of_record_level,
      impact_of_issue_level:
        genericRQScoringConfigDefaults.impact_of_issue_level,
      factor_low: genericRQScoringConfigDefaults.factor_low,
      factor_medium: genericRQScoringConfigDefaults.factor_medium,
      factor_high: genericRQScoringConfigDefaults.factor_high,
    });
    setSeverityMappings({});
  };

  // Severity mapping handlers
  const severityEntries = Object.entries(severityMappings);

  const handleAddMapping = () => {
    const tempKey = `__new_${newMappingCounter.current++}`;
    setSeverityMappings((prev) => ({ ...prev, [tempKey]: "medium" }));
  };

  const handleRemoveMapping = (queryName: string) => {
    setSeverityMappings((prev) => {
      const next = { ...prev };
      delete next[queryName];
      return next;
    });
  };

  const handleUpdateQueryName = (oldName: string, newName: string) => {
    if (newName === oldName) return;
    setSeverityMappings((prev) => {
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([k, v]) => {
        next[k === oldName ? newName : k] = v;
      });
      return next;
    });
  };

  const handleUpdateSeverity = (queryName: string, severity: string) => {
    setSeverityMappings((prev) => ({ ...prev, [queryName]: severity }));
  };

  /**
   * Custom onChange / onBlur for numeric <input type="number"> fields.
   *
   * DQ-8556: non-empty NaN (intermediate browser state) leaves Formik untouched
   * so the browser input buffer persists.
   * DQ-8521: explicit empty string (user cleared all digits) is committed to
   * Formik so the field does not snap back and Yup validation fires on blur.
   */
  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value === "") {
      formik.setFieldValue(name, "");
      return;
    }
    const v = parseFloat(value);
    if (!isNaN(v)) {
      formik.setFieldValue(name, v);
    }
  };

  const handleNumericBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    formik.handleBlur(e);
    if (isNaN(parseFloat(e.target.value))) {
      formik.setFieldValue(e.target.name, "");
    }
  };

  const impactSumError = getImpactSumError(formik.errors);

  const factorField = (
    label: string,
    name: "factor_low" | "factor_medium" | "factor_high",
  ) => (
    <Grid item xs={12} sm={6} md={4}>
      <label className="label-text">
        {label}
        <span className="required-asterisk">*</span>
      </label>
      <TextField
        name={name}
        type="number"
        inputProps={{ step: 0.05 }}
        value={formik.values[name]}
        onChange={handleNumericChange}
        onBlur={handleNumericBlur}
        className={`form-control ${formik.errors[name] ? "has-error" : ""}`}
        error={Boolean(formik.errors[name])}
        helperText={(formik.errors[name] as string) || " "}
      />
    </Grid>
  );

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <Box>
        <form onSubmit={formik.handleSubmit} autoComplete="off">
          <Box className="text-box-card-white m-0">
            <Box className="text-box-header">
              <h3>{isEditMode ? "Edit" : "Add"} Generic RQ Scoring Config</h3>
            </Box>
            <Grid
              container
              rowSpacing={2.5}
              columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
            >
              <Grid item xs={12} sm={6} md={4}>
                {isEditMode ? (
                  <>
                    <label className="label-text">Generic Research Query</label>
                    <TextField
                      fullWidth
                      value={
                        grqName
                          ? `(${genericResearchQueryId}) ${grqName}`
                          : genericResearchQueryId
                            ? `(${genericResearchQueryId})`
                            : ""
                      }
                      InputProps={{ readOnly: true }}
                      className="form-control"
                    />
                  </>
                ) : (
                  <AutoCompleteGenericResearchQueryId
                    setIsLoading={setIsLoading}
                    handelOnChangeGenericResearchQuery={handleChangeGRQ}
                    currentGenericResearchQueryId={
                      genericResearchQueryId || null
                    }
                    filterByScoreConfigAssociated={false}
                    required
                    error={Boolean(grqError)}
                    helperText={grqError}
                    className={`form-control-autocomplete ${
                      grqError ? "has-error" : ""
                    }`}
                  />
                )}
              </Grid>
            </Grid>
            <Grid
              container
              rowSpacing={2.5}
              columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
              sx={{ mt: "20px" }}
            >
              <Grid item xs={12} md={6}>
                <label className="label-text">
                  Impact of Record Level:{" "}
                  {(Number(formik.values.impact_of_record_level) || 0).toFixed(
                    2,
                  )}
                </label>
                <Slider
                  name="impact_of_record_level"
                  value={Number(formik.values.impact_of_record_level) || 0}
                  onChange={(_, val) =>
                    formik.setFieldValue("impact_of_record_level", val)
                  }
                  min={0}
                  max={1}
                  step={0.05}
                  marks={IMPACT_MARKS}
                  valueLabelDisplay="auto"
                  sx={SLIDER_SX}
                />
                {formik.touched.impact_of_record_level &&
                  formik.errors.impact_of_record_level &&
                  formik.errors.impact_of_record_level !== impactSumError && (
                    <Typography color="error" variant="caption">
                      {formik.errors.impact_of_record_level as string}
                    </Typography>
                  )}
              </Grid>
              <Grid item xs={12} md={6}>
                <label className="label-text">
                  Impact of Issue Level:{" "}
                  {(Number(formik.values.impact_of_issue_level) || 0).toFixed(
                    2,
                  )}
                </label>
                <Slider
                  name="impact_of_issue_level"
                  value={Number(formik.values.impact_of_issue_level) || 0}
                  onChange={(_, val) =>
                    formik.setFieldValue("impact_of_issue_level", val)
                  }
                  min={0}
                  max={1}
                  step={0.05}
                  marks={IMPACT_MARKS}
                  valueLabelDisplay="auto"
                  sx={SLIDER_SX}
                />
                {formik.touched.impact_of_issue_level &&
                  formik.errors.impact_of_issue_level &&
                  formik.errors.impact_of_issue_level !== impactSumError && (
                    <Typography color="error" variant="caption">
                      {formik.errors.impact_of_issue_level as string}
                    </Typography>
                  )}
              </Grid>
              {impactSumError && (
                <Grid item xs={12}>
                  <Typography color="error" variant="body2">
                    {impactSumError}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Grid
              container
              rowSpacing={2.5}
              columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
              sx={{ mt: "20px" }}
            >
              {factorField("Factor (Low)", "factor_low")}
              {factorField("Factor (Medium)", "factor_medium")}
              {factorField("Factor (High)", "factor_high")}
            </Grid>

            {/* Severity Mappings Section */}
            <Typography
              variant="body2"
              sx={{ mt: "20px", mb: 1, fontWeight: 600 }}
            >
              Severity Mappings (query_name to severity)
            </Typography>
            <Box
              sx={{
                border: "1px solid var(--table-border)",
                borderRadius: "4px",
                mb: 2,
                overflow: "hidden",
              }}
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Query Name</strong>
                      </TableCell>
                      <TableCell sx={{ width: 160 }}>
                        <strong>Severity</strong>
                      </TableCell>
                      <TableCell align="center" sx={{ width: 60 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {severityEntries.map(([queryName, severity]) => {
                      const usedNames = Object.keys(severityMappings).filter(
                        (n) => n !== queryName,
                      );
                      const availableOptions = queryNameOptions.filter(
                        (opt) => !usedNames.includes(opt),
                      );
                      return (
                        <TableRow key={queryName}>
                          <TableCell>
                            <Autocomplete
                              value={
                                queryName.startsWith("__new_")
                                  ? null
                                  : queryName || null
                              }
                              options={availableOptions}
                              onChange={(_e, newValue) =>
                                handleUpdateQueryName(queryName, newValue || "")
                              }
                              className="form-control-autocomplete dropdown-padding-1"
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  className="form-control"
                                  placeholder="Select query name"
                                />
                              )}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ width: 160 }}>
                            <Select
                              fullWidth
                              value={severity}
                              onChange={(e) =>
                                handleUpdateSeverity(queryName, e.target.value)
                              }
                              size="small"
                            >
                              {scoringSeverityOptions.map((opt: string) => (
                                <MenuItem key={opt} value={opt}>
                                  {opt}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveMapping(queryName)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <Box sx={{ p: 1, borderTop: "1px solid var(--table-border)" }}>
                  <Button
                    size="small"
                    className="btn-orange btn-sm"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddMapping}
                  >
                    Add Mapping
                  </Button>
                </Box>
              </TableContainer>
            </Box>
          </Box>
          <FlexBetween gap={2.5} sx={{ mt: 1, justifyContent: "flex-end" }}>
            <Button
              type="button"
              className="btn-orange btn-dark"
              variant="contained"
              disabled={formik.isSubmitting || isLoading}
              onClick={handleResetToDefaults}
              startIcon={<RestoreIcon />}
            >
              Reset to Defaults
            </Button>
            <ViewJsonSplitButton
              onViewJson={() => setIsPayloadDialogOpen(true)}
              onCopyJson={handleCopyPayload}
            />
            <Button
              type="submit"
              className="btn-orange"
              color="secondary"
              variant="contained"
              disabled={isScoringSubmitDisabled({
                isSubmitting: formik.isSubmitting,
                isLoading,
                isValid: formik.isValid,
                isEditMode,
                hasChanges:
                  formik.dirty ||
                  JSON.stringify(initialSeverityMappings) !==
                    JSON.stringify(
                      Object.fromEntries(
                        Object.entries(severityMappings).filter(
                          ([k]) => k !== "" && !k.startsWith("__new_"),
                        ),
                      ),
                    ),
              })}
              startIcon={
                formik.isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveOutlinedIcon />
                )
              }
            >
              Save
            </Button>
          </FlexBetween>
        </form>
      </Box>
      <CommentBeforeUpdateDialog
        title="Confirm updating Generic RQ Scoring Config"
        dialogContent={
          <div>
            <p className="m-0 mb-2">Add a note before updating</p>
            <textarea
              value={comment}
              className="form-control-1 max-60"
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        }
        openConfirmation={openComment}
        handleSaveComment={handleSaveComment}
        handleCancel={() => {
          setOpenComment(false);
          setComment("");
          setPendingPayload(null);
        }}
      />
      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        dialogTitle="Generic RQ Scoring Config Payload"
        payload={buildPayload()}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </>
  );
};

export default AddEditGenericRQScoring;
